import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, healthAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('midas_token'));
  const [loading, setLoading] = useState(true);
  const [systemInfo, setSystemInfo] = useState(null);
  const [reviewMode, setReviewMode] = useState(false);

  // Consulta del estado del sistema (salud, reviewMode y entorno)
  const checkHealth = useCallback(async () => {
    try {
      const res = await healthAPI.check();
      if (res.data) {
        setSystemInfo(res.data);
        setReviewMode(Boolean(res.data.reviewMode));
      }
    } catch (error) {
      console.warn('Error al verificar estado de salud del sistema:', error.message);
    }
  }, []);

  useEffect(() => {
    checkHealth();
  }, [checkHealth]);

  // Carga inicial y validación de sesión
  useEffect(() => {
    async function loadUserSession() {
      const storedToken = localStorage.getItem('midas_token');
      if (!storedToken) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const response = await authAPI.me();
        if (response.data?.data) {
          setUser(response.data.data);
          setToken(storedToken);
        } else {
          throw new Error('Respuesta inválida');
        }
      } catch (error) {
        console.warn('Sesión expirada o no válida:', error.message);
        localStorage.removeItem('midas_token');
        setUser(null);
        setToken(null);
      } finally {
        setLoading(false);
      }
    }

    loadUserSession();
  }, []);

  const login = useCallback(async (credentials) => {
    const response = await authAPI.login(credentials);
    const { token: jwtToken, usuario } = response.data.data;

    localStorage.setItem('midas_token', jwtToken);
    setToken(jwtToken);
    setUser(usuario);
    return usuario;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('midas_token');
    setToken(null);
    setUser(null);
  }, []);

  const updateUser = useCallback((updatedData) => {
    setUser((prev) => (prev ? { ...prev, ...updatedData } : prev));
  }, []);

  const hasPermission = useCallback(
    (permisoCodigo) => {
      if (!user) return false;
      if (user.rol?.codigo === 'ADMIN') return true;
      return Array.isArray(user.permisos) && user.permisos.includes(permisoCodigo);
    },
    [user]
  );

  const hasRole = useCallback(
    (rolCodigo) => {
      if (!user) return false;
      if (user.rol?.codigo === 'ADMIN') return true;
      return user.rol?.codigo === rolCodigo;
    },
    [user]
  );

  const value = {
    user,
    token,
    loading,
    systemInfo,
    reviewMode,
    // Ambiente declarado por el backend (MIDAS_ENV): 'DEV' | 'PRODUCCION' | 'LOCAL'.
    ambiente: systemInfo?.ambiente || null,
    checkHealth,
    isAuthenticated: Boolean(user && token),
    login,
    logout,
    updateUser,
    hasPermission,
    hasRole
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser utilizado dentro de un AuthProvider');
  }
  return context;
}
